/** Lightweight safe markdown → HTML for tool results (no deps). */

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inlineFormat(raw: string): string {
  let s = esc(raw);
  s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  return s;
}

/** Convert a subset of markdown (headings, lists, bold, code) to HTML. */
export function markdownToHtml(md: string): string {
  const src = (md || "").replace(/\r\n/g, "\n").trim();
  if (!src) return "";

  const lines = src.split("\n");
  const parts: string[] = [];
  let i = 0;
  let para: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let listItems: string[] = [];

  const flushPara = () => {
    if (!para.length) return;
    parts.push(`<p>${inlineFormat(para.join(" "))}</p>`);
    para = [];
  };

  const flushList = () => {
    if (!listType || !listItems.length) {
      listType = null;
      listItems = [];
      return;
    }
    parts.push(
      `<${listType}>${listItems.map((t) => `<li>${inlineFormat(t)}</li>`).join("")}</${listType}>`
    );
    listType = null;
    listItems = [];
  };

  while (i < lines.length) {
    const line = lines[i]!;
    const trimmed = line.trim();

    if (!trimmed) {
      flushPara();
      flushList();
      i++;
      continue;
    }

    if (/^```/.test(trimmed)) {
      flushPara();
      flushList();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i]!.trim())) {
        code.push(lines[i]!);
        i++;
      }
      i++;
      parts.push(`<pre><code>${esc(code.join("\n"))}</code></pre>`);
      continue;
    }

    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      flushPara();
      flushList();
      const level = heading[1]!.length;
      parts.push(`<h${level}>${inlineFormat(heading[2]!)}</h${level}>`);
      i++;
      continue;
    }

    const ul = trimmed.match(/^[-*•]\s+(.+)$/);
    if (ul) {
      flushPara();
      if (listType && listType !== "ul") flushList();
      listType = "ul";
      listItems.push(ul[1]!);
      i++;
      continue;
    }

    const ol = trimmed.match(/^\d+[.)]\s+(.+)$/);
    if (ol) {
      flushPara();
      if (listType && listType !== "ol") flushList();
      listType = "ol";
      listItems.push(ol[1]!);
      i++;
      continue;
    }

    if (/^---+$/.test(trimmed)) {
      flushPara();
      flushList();
      parts.push("<hr />");
      i++;
      continue;
    }

    flushList();
    para.push(trimmed);
    i++;
  }

  flushPara();
  flushList();
  return parts.join("\n");
}

/** Plain-text fallback from structured legal-risk result. */
export function legalRiskToMarkdown(result: {
  overview?: string;
  categories?: Array<{
    name: string;
    items: Array<{ title: string; detail: string; severity?: string }>;
  }>;
}): string {
  const parts: string[] = [];
  if (result.overview?.trim()) parts.push(result.overview.trim());
  for (const cat of result.categories ?? []) {
    parts.push(`## ${cat.name}`);
    for (const it of cat.items) {
      const sev =
        it.severity === "high"
          ? "بالا"
          : it.severity === "medium"
            ? "متوسط"
            : it.severity === "low"
              ? "پایین"
              : "";
      parts.push(
        `- **${it.title}**${sev ? ` · ${sev}` : ""}\n  ${it.detail}`
      );
    }
  }
  return parts.join("\n\n");
}
